import { useMemo } from "react"

// hooks
import {
    getVisualizationFrequencyTupleFieldEnum,
    getVisualizationDateField,
    getVisualizationXAxisLabel,
} from "app/static/frontend/widgets/components/visualization/hooks/visualizationAxesHooks"

/**
 * Given the backend data returned from x_frequency_tuples
 * (or the default group_by in app/widgets/engine.py DataSourceEngine group),
 * this getter applies enum labels (if necessary) to the "name" key and logically groups
 * the value (the number returned from the group_by) into a format that is easy to
 * render in a d3 visualization.
 *
 * @name useBarVisualizationData
 * @function
 * @param {Object | Array[Object]} data - the data returned by the group_by
 * @param {Array[Object]} dataSources - the widget.data_sources returned by the group_by
 * @param {string} groupKey - the groupKey of the Bar visualization (usually "name")
 * @param {Array[string]} labels - the labels of the selected datasets (safedSearches)
 * @param {Enum} quorumDataType - the widget.data_sources[0].safed_search.data_type QuorumDataType enum returned from the group_by
 *
 * @returns {Array[Object]} Returns a logically grouped Array of Objects that can be easily parsed
 * by all of the Visualization widget types (DjangIO.app.widgets.types.VisualizationSubtype)
 */
export const useBarVisualizationData = ({
    data,
    dataSources,
    groupKey,
    labels,
    quorumDataType,
    stancesWithCustomLabels,
}) =>
    useMemo(() => {
        // the Enum of the data keys, if applicable
        const fieldEnum = getVisualizationFrequencyTupleFieldEnum({
            dataSources,
            quorumDataType,
        })

        const dateField = getVisualizationDateField({
            dataSources,
        })

        if (data) {
            // the widget_engine returns an array of objects if the data is > length 1
            if (Array.isArray(data)) {
                // [
                //     {
                //         4: { agg: 10 },
                //         5: { agg: 20 },
                //     },
                //     {
                //         4: { agg: 30 },
                //         5: { agg: 40 },
                //     },
                // ]
                // to
                // [
                //     {
                //         [groupKey]: 4,
                //         [`${groupKey}Original`]: 4,
                //         labels[0]: 10,
                //         labels[1]: 30,
                //     },
                //     {
                //         [groupKey]: 5,
                //         [`${groupKey}Original`]: 5,
                //         labels[0]: 20,
                //         labels[1]: 40,
                //     },
                // ]
                // or
                // [
                //     {
                //         [groupKey]: "S.Res." (BillType.senate_resolution),
                //         [`${groupKey}Original`]: 4,
                //         labels[0]: 10,
                //         labels[1]: 30,
                //     },
                //     {
                //         [groupKey]: "H.Con.Res." (BillType.house_concurrent_resolution),
                //         [`${groupKey}Original`]: 5,
                //         labels[0]: 20,
                //         labels[1]: 40,
                //     },
                // ]
                // if dataSources[0].group_by.frequency_tuple_field === FrequencyTupleFields.bill_types.value
                // ("app.bill.models.BillType")
                return Object.values(
                    data.reduce((accumulator, item, dataIndex) => {
                        Object
                            .entries(item)
                            .sort((a, b) => b[1].agg - a[1].agg)
                            .forEach(([idOrEnumValue, groupKeyValueAndAgg]) => {
                                const { groupKeyValue, agg } = groupKeyValueAndAgg
                                const field = groupKeyValue || idOrEnumValue

                                accumulator[field] = {
                                    ...accumulator[field],
                                    ...{
                                        [groupKey]: (
                                            getVisualizationXAxisLabel({
                                                dateField,
                                                // there are two types of x_frequency_tuples methods that we can invoke for Bar Visualizations
                                                // 1) returns a QuerySet of entries where each item is a tuple of lenght 3 containing
                                                // (id (3000002, for example), name/title/x-axis display string, agg)
                                                // 2) returns a QuerySet of entries where each item is a tuple of lenght 2 containing
                                                // (enum value (Party.Democrat.value, for example), agg)

                                                // groupKey is used in case 1 ("Sen. Lamar Alexander (R-TN)")
                                                // idOrEnumValue is used in case 2 for the enumValue (Party.Democrat.value)
                                                // see app/widgets/engine.py _evaluate
                                                field,
                                                fieldEnum,
                                                stancesWithCustomLabels,
                                            })
                                        ),
                                        [`${groupKey}Original`]: idOrEnumValue,
                                        [`${labels[dataIndex]}-${dataIndex}`]: agg,
                                    }
                                }
                            })

                        return accumulator
                    }, {})
                )
            // the widget_engine returns a non-array object if the data is of length 1
            } else {
                // convert
                // {
                //     4: { agg: 10 },
                //     5: { agg: 20 },
                // }
                // to
                // [
                //     {[groupKey]: 4, [`${groupKey}Original`]: 4, labels[0]: 10},
                //     {[groupKey]: 5, [`${groupKey}Original`]: 5, labels[0]: 20},
                // ]
                // or
                // [
                //      {[groupKey]: "S.Res." (BillType.senate_resolution), [`${groupKey}Original`]: 4, labels[0]: 10},
                //      {[groupKey]: "H.Con.Res." (BillType.house_concurrent_resolution), [`${groupKey}Original`]: 5, labels[0]: 20},
                // ]
                // if dataSources[0].group_by.frequency_tuple_field === FrequencyTupleFields.bill_types.value
                // ("app.bill.models.BillType")
                return (
                    Object
                        .entries(data)
                        .sort((a, b) => b[1].agg - a[1].agg)
                        .map(([idOrEnumValue, groupKeyValueAndAgg]) => {
                            const { groupKeyValue, agg } = groupKeyValueAndAgg
                            const field = groupKeyValue || idOrEnumValue

                            return {
                                [groupKey]: (
                                    getVisualizationXAxisLabel({
                                        dateField,
                                        // there are two types of x_frequency_tuples methods that we can invoke for Bar Visualizations
                                        // 1) returns a QuerySet of entries where each item is a tuple of lenght 3 containing
                                        // (id (3000002, for example), name/title/x-axis display string, agg)
                                        // 2) returns a QuerySet of entries where each item is a tuple of lenght 2 containing
                                        // (enum value (Party.Democrat.value, for example), agg)

                                        // groupKey is used in case 1 ("Sen. Lamar Alexander (R-TN)")
                                        // idOrEnumValue is used in case 2 for the enumValue (Party.Democrat.value)
                                        // see app/widgets/engine.py _evaluate
                                        field,
                                        fieldEnum,
                                        stancesWithCustomLabels,
                                    })
                                ),
                                [`${groupKey}Original`]: idOrEnumValue,
                                [`${labels[0]}-0`]: agg,
                            }
                        })
                )
            }
        }

        return []
    }, [data, labels])
